import { STATUS_HINT, STATUS_META } from "./status";
import type { Owner, RequestStatus, RequestSummary } from "./types";

/**
 * How a request list is grouped.
 *
 * Kept out of the component so the bucketing is unit-testable without a DOM,
 * and so the one real difference between the two modes — what the headings
 * are — lives in data rather than in a branch inside the renderer.
 */
export interface GroupSpec<T> {
  /** Namespace for each rows container's DOM id: `${scope}-group-${key}`. */
  scope: string;
  /** Where this view's folds are remembered, e.g. "collapsed-statuses". */
  storageKey: string;
  build: (rows: T[]) => RequestGroup<T>[];
  /** True when the row's person column should show status instead of the
   *  account lead — the grouping already names the owner. */
  showStatus?: boolean;
}

/** One heading and the rows beneath it. */
export interface RequestGroup<T> {
  /** Stable id: the DOM id suffix and the collapse key. */
  key: string;
  label: string;
  /** Hover text explaining the heading, where the name alone does not. */
  hint?: string;
  rows: T[];
}

/** The bucket for requests nobody owns. Underscores cannot collide with a
 *  member id, which is always a slug of `[a-z0-9-]`. */
export const UNASSIGNED = "__unassigned";

/**
 * Group by status: only the statuses actually present, in lifecycle order.
 *
 * Present-only is deliberate and unchanged — showing all eleven statuses on
 * every list would be a regression. The hint carries STATUS_HINT, which is
 * where those definitions live now the Status column is gone.
 */
export function statusGroups<T extends { status: RequestStatus }>(scope: string, audience: "staff" | "customer"): GroupSpec<T> {
  return {
    scope,
    storageKey: "collapsed-statuses",
    build: (rows) => {
      const present = [...new Set(rows.map((r) => r.status))].sort((a, b) => STATUS_META[a].order - STATUS_META[b].order);
      return present.map((s) => ({
        key: s,
        label: STATUS_META[s].label,
        hint: audience === "customer" ? (STATUS_HINT[s]?.customer ?? STATUS_HINT[s]?.staff) : STATUS_HINT[s]?.staff,
        rows: rows.filter((r) => r.status === s),
      }));
    },
  };
}

/**
 * Group by technical owner: a heading for every member passed in AND for
 * anyone who owns a row, busiest first, with everything unowned last.
 *
 * Both halves of that domain matter. The roster is what makes an engineer with
 * nothing assigned visible, which is the fact this view exists to show; the
 * rows are what guarantee no owned request can go unlisted. Ordering matches
 * the dashboard's workload panel: most open work first, ties by name.
 */
export function ownerGroups(members: Owner[], { includeUnassigned = true } = {}): GroupSpec<RequestSummary> {
  return {
    scope: "staff",
    storageKey: "collapsed-owners",
    showStatus: true,
    build: (rows) => {
      // Headings come from the roster AND from whoever actually owns a row.
      // The roster alone is not enough: it is empty for the frames before
      // /api/team resolves, and stays empty for good if that fetch fails, so
      // every owned request would silently have no heading to live under —
      // a view whose job is to account for all the work, quietly showing less
      // of it. A row carries its own owner, so it can always name its group.
      const byId = new Map(members.map((m) => [m.id, m]));
      for (const r of rows) if (r.owner && !byId.has(r.owner.id)) byId.set(r.owner.id, r.owner);

      const groups = [...byId.values()]
        .map((m) => ({ key: m.id, label: m.name, rows: rows.filter((r) => r.owner?.id === m.id) }))
        .sort((a, b) => b.rows.length - a.rows.length || a.label.localeCompare(b.label));

      if (!includeUnassigned) return groups;
      // Appended after the sort, never inside it, so it stays last whatever
      // the comparator later becomes. Only genuinely ownerless rows: a request
      // whose owner_id no longer resolves arrives as owner: null from the
      // server (src/db/requestList.ts), which is the same case, not a new one.
      return [...groups, { key: UNASSIGNED, label: "Unassigned", rows: rows.filter((r) => r.owner === null) }];
    },
  };
}
